<template>
  <Teleport to="body">
    <Transition name="webinar-replay-modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="webinar-replay-title"
        @click.self="close"
      >
        <section class="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#17191f] text-white shadow-2xl">
          <header class="flex items-start justify-between gap-5 border-b border-white/10 px-5 py-4 sm:px-6">
            <div class="min-w-0">
              <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.13em] text-blue-300">
                <PlayCircleIcon class="h-4 w-4" />
                Webinar replay
              </div>
              <h2 id="webinar-replay-title" class="mt-1 truncate text-lg font-bold sm:text-xl">
                {{ event?.title || 'Completed webinar' }}
              </h2>
              <p class="mt-1 text-xs text-zinc-400">{{ completedLabel }}</p>
            </div>
            <button
              type="button"
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/5 text-zinc-300 transition hover:bg-white/10 hover:text-white"
              aria-label="Close replay"
              @click="close"
            >
              <XMarkIcon class="h-6 w-6" />
            </button>
          </header>

          <div class="min-h-0 overflow-y-auto p-4 sm:p-6">
            <div class="webinar-replay-player relative w-full overflow-hidden rounded-xl border border-white/10 bg-black shadow-2xl">
              <div
                v-if="replayLoading"
                class="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950"
                role="status"
                aria-live="polite"
              >
                <div class="replay-skeleton absolute inset-0 opacity-40"></div>
                <span class="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/10">
                  <ArrowPathIcon class="h-8 w-8 animate-spin text-blue-300" />
                </span>
                <p class="relative z-10 mt-4 text-sm font-semibold text-zinc-200">Loading Webinar replay...</p>
              </div>

              <WebinarReplayVideoPlayer
                v-else-if="replayUrl"
                :key="replayUrl"
                :src="replayUrl"
                :poster="event?.image || ''"
                class="absolute inset-0 h-full w-full"
              />

              <template v-else>
                <img
                  v-if="event?.image"
                  :src="event.image"
                  :alt="`${event?.title || 'Webinar'} thumbnail`"
                  class="absolute inset-0 h-full w-full object-contain opacity-25"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/55"></div>
                <div class="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <span class="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/10">
                    <FilmIcon class="h-8 w-8 text-zinc-300" />
                  </span>
                  <h3 class="mt-5 text-lg font-bold text-white">{{ unavailableTitle }}</h3>
                  <p class="mt-2 max-w-lg text-sm leading-6 text-zinc-400">{{ unavailableMessage }}</p>
                </div>
              </template>
            </div>

            <footer class="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm font-semibold text-zinc-200">{{ event?.hostName || 'Webinar host' }}</p>
                <p class="mt-1 text-xs text-zinc-500">
                  {{ replayFooterStatus }}
                </p>
              </div>

              <a
                v-if="downloadUrl"
                :href="downloadUrl"
                :download="downloadFilename"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-5 py-3 text-sm font-bold text-white transition hover:bg-blue-600"
              >
                <ArrowDownTrayIcon class="h-5 w-5" />
                Download MP4
              </a>
              <button
                v-else
                type="button"
                class="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-white/5 px-5 py-3 text-sm font-bold text-zinc-500"
                disabled
                title="The MP4 download will become available after Webinar recording is connected and processed."
              >
                <ArrowDownTrayIcon class="h-5 w-5" />
                Download unavailable
              </button>
            </footer>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, watch } from 'vue';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  FilmIcon,
  PlayCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import WebinarReplayVideoPlayer from '@/Components/WebinarReplayVideoPlayer.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  event: { type: Object, default: null },
});

const emit = defineEmits(['close']);
let previousBodyOverflow = '';
let bodyScrollLocked = false;

const replayUrl = computed(() => String(
  props.event?.replayUrl
  || props.event?.replayMp4Url
  || props.event?.metadata?.replayUrl
  || props.event?.metadata?.replayMp4Url
  || ''
).trim());

const replayStatus = computed(() => String(
  props.event?.replayStatus || props.event?.recordingStatus || ''
).trim().toLowerCase());

const replayLoading = computed(() => Boolean(props.event?.replayLoading));
const processingStatuses = new Set(['starting', 'recording', 'finalizing', 'processing']);

const downloadUrl = computed(() => String(
  props.event?.replayDownloadUrl
  || props.event?.metadata?.replayDownloadUrl
  || replayUrl.value
  || ''
).trim());

const unavailableTitle = computed(() => (
  props.event?.recordReplay === false
    ? 'This webinar was not recorded'
    : processingStatuses.has(replayStatus.value)
      ? 'Your Webinar replay is processing'
      : replayStatus.value === 'failed'
        ? 'This replay could not be processed'
        : 'Replay is not available yet'
));

const unavailableMessage = computed(() => (
  props.event?.recordReplay === false
    ? 'Recording was disabled for this event. You can still clone the Webinar and enable recording for the next broadcast.'
    : processingStatuses.has(replayStatus.value)
      ? 'Platforma captured the broadcast and is creating the optimized MP4 and HLS replay. This window updates automatically.'
      : replayStatus.value === 'failed'
        ? (props.event?.replayError || 'Platforma was unable to finish this recording. The failure has been retained for diagnosis.')
        : 'No recording was found for this completed Webinar.'
));

const replayFooterStatus = computed(() => {
  if (replayLoading.value) return 'Loading recording status';
  if (replayUrl.value) return 'Replay ready';
  if (props.event?.recordReplay === false) return 'Recording disabled';
  if (processingStatuses.has(replayStatus.value)) return 'Processing replay';
  if (replayStatus.value === 'failed') return 'Recording failed';
  return 'Replay unavailable';
});

const completedLabel = computed(() => {
  const value = props.event?.endedAt || props.event?.scheduledStartAt;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Completed event';
  return `Completed ${new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)}`;
});

const downloadFilename = computed(() => {
  const base = String(props.event?.title || 'webinar-replay')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'webinar-replay';
  return `${base}.mp4`;
});

function close() {
  emit('close');
}

function handleKeydown(event) {
  if (props.open && event.key === 'Escape') close();
}

watch(() => props.open, (isOpen) => {
  if (typeof document === 'undefined') return;
  if (isOpen && !bodyScrollLocked) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    bodyScrollLocked = true;
  } else if (!isOpen && bodyScrollLocked) {
    document.body.style.overflow = previousBodyOverflow;
    bodyScrollLocked = false;
  }
}, { immediate: true });

if (typeof window !== 'undefined') window.addEventListener('keydown', handleKeydown);

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('keydown', handleKeydown);
  if (typeof document !== 'undefined' && bodyScrollLocked) {
    document.body.style.overflow = previousBodyOverflow;
  }
});
</script>

<style scoped>
.webinar-replay-modal-enter-active,
.webinar-replay-modal-leave-active {
  transition: opacity 180ms ease;
}

.webinar-replay-modal-enter-active section,
.webinar-replay-modal-leave-active section {
  transition: transform 180ms ease, opacity 180ms ease;
}

.webinar-replay-modal-enter-from,
.webinar-replay-modal-leave-to {
  opacity: 0;
}

.webinar-replay-modal-enter-from section,
.webinar-replay-modal-leave-to section {
  opacity: 0;
  transform: translateY(12px) scale(0.985);
}

.replay-skeleton {
  background: linear-gradient(110deg, #09090b 25%, #27272a 40%, #09090b 55%);
  background-size: 220% 100%;
  animation: replay-shimmer 1.35s ease-in-out infinite;
}

.webinar-replay-player {
  aspect-ratio: 16 / 9;
  min-height: 220px;
  flex: 0 0 auto;
}

@media (max-width: 640px) {
  .webinar-replay-player {
    min-height: 180px;
  }
}

@keyframes replay-shimmer {
  to {
    background-position-x: -220%;
  }
}
</style>
